/**
 * The operator's name for a workflow action type, in the words the workflow
 * editor's action picker already uses.
 *
 * Consumers: that picker (`WorkflowActionsEditor`), and the two surfaces that
 * report where a failed run stopped — `formatWorkflowErrorMessage` and the
 * parameter sidebar's failure banner. Both printed the identifier before this map,
 * so an operator read `Action: callSignifyApi (2)` (SYD-2445).
 *
 * The argument is a plain string, not `WorkflowAction['actionType']`: the failure
 * surfaces read it off an untrusted wire through `CallableErrorDetails`, where
 * another callable or an older revision can put anything. An unmapped value is
 * returned unchanged — harder to read than a label, better than nothing.
 */

import type { WorkflowAction } from '#types/System.Workflow.types';

/** Every action type, so a new one cannot ship without a name on screen. */
const LABEL_BY_ACTION_TYPE: Record<WorkflowAction['actionType'], string> = {
  updateCells: 'Update table cells',
  updateTableRows: 'Update rows by filter (table)',
  setFormField: 'Set form field',
  setEntityReference: 'Set entity reference',
  createForm: 'Create new form',
  copyRows: 'Copy rows to new form',
  duplicateRows: 'Duplicate rows (in-place)',
  syncRowsFromReferences: 'Sync rows from references',
  syncRowsByFieldValue: 'Sync rows by field value',
  insertRows: 'Insert rows',
  setFormState: 'Set form state',
  lockRows: 'Lock rows',
  send: 'Send email',
  savePdfToForm: 'Save PDF to form',
  callSignifyApi: 'Call Signify API',
  callLanguageModel: 'Call language model',
};

/** The same map read by an unvalidated key, without asserting the union. */
const labelByUnvalidatedActionType: Record<string, string> = LABEL_BY_ACTION_TYPE;

export const workflowActionTypeLabel = (actionType: string): string =>
  labelByUnvalidatedActionType[actionType] ?? actionType;
